import useTranslation from 'hooks/useTranslation'
import { toLongDate, toTime } from 'utils/date'
import React from 'react'
import './KeyReturn.scss'

type Translate = (key?: string) => string
type Lang = 'pl' | 'en'

/** "14 października 2026 godz. 12:00" */
export const toKeyReturnDate = (date: string | null | undefined, t: Translate, lang: Lang) => {
  if (!date) return ''
  return `${toLongDate(date, lang)} ${t('at')} ${toTime(date, lang)}`
}

export const fillKeyReturnText = (text: string, values: Record<string, string>) => {
  return Object.entries(values).reduce((result, [key, value]) => result.split(`{${key}}`).join(value), text)
}

const getCabinetName = ({ name, location }: warehouse.KeyReturnCabinet) => {
  return location ? `${name} (${location})` : name
}

interface KeyReturnProps {
  keyReturn?: warehouse.KeyReturn
}

/**
 * Sekcja "Zwrot klucza" - tylko informacja dla klienta.
 * Otwarcie szafki nadal idzie przez actions.key_cabinet / POST /v1/keycabinets/access.
 */
const KeyReturn: React.FC<KeyReturnProps> = ({ keyReturn }) => {
  const { t, lang } = useTranslation('warehouse')

  if (!keyReturn?.required || !keyReturn.items?.length) return null

  const showNumber = keyReturn.items.length > 1

  return <div className='keyReturn__section'>
    <div className='title'>
      <div>{t('keyReturn.title')}</div>
      <div>{t('keyReturn.subtitle')}</div>
    </div>

    <div className='keyReturn'>
      {keyReturn.items.map(item => {
        const { warehouseId, warehouseNumber, method, methodLabel, instruction, imageUrl, window: returnWindow, hasAccess, cabinet } = item
        const until = toKeyReturnDate(returnWindow?.until, t, lang)
        const from = toKeyReturnDate(returnWindow?.from, t, lang)
        const isCabinet = method === 'key_cabinet'

        return <div
          key={`${warehouseId}-${cabinet?.id}`}
          className='keyReturn__item'>
          <div className='keyReturn__header'>
            <i className='icon icon--key' />
            <span>{methodLabel}{showNumber ? ` - ${warehouseNumber}` : ''}</span>
          </div>

          <div className='keyReturn__text'>
            {isCabinet
              ? <>
                {fillKeyReturnText(t('keyReturn.cabinet'), { cabinet: getCabinetName(cabinet) })}
                {' '}
                {from
                  ? fillKeyReturnText(t('keyReturn.cabinetWindow'), { from, until })
                  : fillKeyReturnText(t('keyReturn.cabinetUntil'), { until })}
              </>
              : fillKeyReturnText(t('keyReturn.dropBox'), { until })}
          </div>

          {isCabinet && !hasAccess && <div className='keyReturn__note'>
            {t('keyReturn.accessSoon')}
          </div>}

          {instruction && <div className='keyReturn__instruction'>{instruction}</div>}

          {imageUrl && <a
            className='keyReturn__image'
            href={imageUrl}
            target='_blank'
            rel='noopener noreferrer'>
            <img
              src={imageUrl}
              loading='lazy'
              alt={t(isCabinet ? 'keyReturn.cabinetImage' : 'keyReturn.dropBoxImage')}
            />
          </a>}
        </div>
      })}
    </div>
  </div>
}

export default KeyReturn
